import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus } from "lucide-react";
import {
  getSecrets,
  revealSecret,
  createSecret,
  updateSecret,
  deleteSecret,
  type AISecret,
} from "@/services/admin-api";
import {
  createSecretSchema,
  updateSecretSchema,
  type CreateSecretInput,
  type UpdateSecretInput,
} from "@/lib/schemas/ai-schemas";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

export default function AISecrets() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [updateTarget, setUpdateTarget] = useState<AISecret | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AISecret | null>(null);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [revealingId, setRevealingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["ai-secrets"],
    queryFn: getSecrets,
  });

  const secrets = data?.data ?? [];

  // --- Create ---
  const createForm = useForm<z.input<typeof createSecretSchema>, unknown, CreateSecretInput>({
    resolver: zodResolver(createSecretSchema),
    defaultValues: { id: "", category: "", name: "", value: "", description: "", isSecret: true },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateSecretInput) => createSecret(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-secrets"] });
      toast({ title: "Secret created" });
      setCreateOpen(false);
      createForm.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create secret",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // --- Update ---
  const updateForm = useForm<UpdateSecretInput>({
    resolver: zodResolver(updateSecretSchema),
    defaultValues: { value: "" },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) => updateSecret(id, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-secrets"] });
      toast({ title: "Secret updated" });
      setUpdateTarget(null);
      updateForm.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update secret",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // --- Delete ---
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSecret(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-secrets"] });
      toast({ title: "Secret deleted" });
      setDeleteTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete secret",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // --- Reveal ---
  const handleReveal = async (id: string) => {
    try {
      setRevealingId(id);
      const response = await revealSecret(id);
      setRevealedValue(response.data.value);
    } catch (error) {
      toast({
        title: "Failed to reveal secret",
        description: (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setRevealingId(null);
    }
  };

  if (isLoading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="App Secrets"
        description="Manage encrypted application secrets"
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Secret
          </Button>
        }
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Last Updated</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {secrets.map((secret) => (
            <TableRow key={secret.id}>
              <TableCell>
                <div className="font-medium">{secret.name}</div>
                <div className="text-xs text-muted-foreground">{secret.id}</div>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <Badge variant="info">{secret.category}</Badge>
              </TableCell>
              <TableCell className="font-mono text-sm text-muted-foreground">
                {secret.isSecret ? "••••••••••••" : secret.maskedValue}
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                {new Date(secret.updatedAt).toLocaleDateString()}
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm space-x-2">
                {secret.isSecret && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReveal(secret.id)}
                    disabled={revealingId === secret.id}
                  >
                    {revealingId === secret.id ? "Revealing..." : "Reveal"}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setUpdateTarget(secret);
                    updateForm.reset({ value: "" });
                  }}
                >
                  Update
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteTarget(secret)}
                  className="text-red-600 hover:text-red-700"
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Reveal Dialog */}
      <Dialog open={revealedValue !== null} onOpenChange={() => setRevealedValue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revealed Secret Value</DialogTitle>
            <DialogDescription>
              This value is sensitive. Close this dialog when done.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-50 p-4 rounded font-mono text-sm break-all">{revealedValue}</div>
          <DialogFooter>
            <Button onClick={() => setRevealedValue(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Secret</DialogTitle>
            <DialogDescription>Create a new encrypted application secret.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={createForm.handleSubmit((data) => createMutation.mutate(data))}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="create-id">ID</Label>
              <Input id="create-id" placeholder="api-key-openai" {...createForm.register("id")} />
              {createForm.formState.errors.id && (
                <p className="text-sm text-red-600">{createForm.formState.errors.id.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-category">Category</Label>
              <Input id="create-category" placeholder="ai" {...createForm.register("category")} />
              {createForm.formState.errors.category && (
                <p className="text-sm text-red-600">
                  {createForm.formState.errors.category.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                placeholder="OpenAI API Key"
                {...createForm.register("name")}
              />
              {createForm.formState.errors.name && (
                <p className="text-sm text-red-600">{createForm.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-value">Value</Label>
              <Textarea id="create-value" placeholder="sk-..." {...createForm.register("value")} />
              {createForm.formState.errors.value && (
                <p className="text-sm text-red-600">{createForm.formState.errors.value.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-description">Description (optional)</Label>
              <Input
                id="create-description"
                placeholder="Used for..."
                {...createForm.register("description")}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="create-isSecret"
                {...createForm.register("isSecret")}
                className="rounded border-gray-300"
              />
              <Label htmlFor="create-isSecret">Encrypt value</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Dialog */}
      <Dialog open={updateTarget !== null} onOpenChange={() => setUpdateTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Secret Value</DialogTitle>
            <DialogDescription>
              Update the value for <strong>{updateTarget?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={updateForm.handleSubmit((data) =>
              updateMutation.mutate({ id: updateTarget!.id, value: data.value }),
            )}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="update-value">New Value</Label>
              <Textarea
                id="update-value"
                placeholder="Enter new value..."
                {...updateForm.register("value")}
              />
              {updateForm.formState.errors.value && (
                <p className="text-sm text-red-600">{updateForm.formState.errors.value.message}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUpdateTarget(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Updating..." : "Update"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Secret"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        onConfirm={() => deleteMutation.mutate(deleteTarget!.id)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
